import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const BattleHistoryTable = ({ heroId, seasonId = null }) => {
  const [battles, setBattles] = useState([]);
  const [stats, setStats] = useState(null);
  const [serverTime, setServerTime] = useState(null);
  const [items, setItems] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const fetchBattleHistory = async (pageNum = 0, reset = false) => {
    try {
      const params = new URLSearchParams({
        limit: '20',
        offset: (pageNum * 20).toString()
      });
      
      if (seasonId) {
        params.append('seasonId', seasonId);
      }

      const response = await fetch(`/api/heroes/${heroId}/history?${params}`);
      console.log('response', response);
      const data = await response.json();
      
      if (data.success) {
        if (reset) {
          setBattles(data.data.battles);
        } else {
          setBattles(prev => [...prev, ...data.data.battles]);
        }
        setStats(data.data.stats);
        setServerTime(data.data.serverTime);
        setItems(data.data.items || {});
        setHasMore(data.data.battles.length === 20);
      }
    } catch (error) {
      console.error('Error fetching battle history:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBattleHistory(0, true);
  }, [heroId, seasonId]);

  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchBattleHistory(nextPage, false);
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatItemIcon = (themeId, iconKey) => {
    if (!themeId || !iconKey) return null;
    
    const awsRegion = process.env.REACT_APP_AWS_REGION || 'us-east-1';
    const s3BasePath = themeId ? `https://soulwars.s3.${awsRegion}.amazonaws.com/themes/${themeId}` : '';
    return `${s3BasePath}/items/${iconKey}.png`;
  };

  const formatTimeElapsed = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    // Use server time if available, otherwise fall back to client time
    const now = serverTime ? new Date(serverTime) : new Date();
    const battleTime = new Date(timestamp);
    const diffMs = now - battleTime;
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `${diffDays}d ago`;
    } else if (diffHours > 0) {
      return `${diffHours}h ago`;
    } else if (diffMinutes > 0) {
      return `${diffMinutes}m ago`;
    } else {
      return 'Just now';
    }
  };

  const getPositionColor = (position) => {
    if (position === 1) return 'text-yellow-400 font-bold';
    if (position <= 3) return 'text-orange-400 font-bold';
    if (position <= 10) return 'text-green-400 font-bold';
    return 'text-gray-400';
  };

  const getPositionText = (position) => {
    if (!position) return 'Eliminated';
    if (position === 1) return '1st';
    if (position === 2) return '2nd';
    if (position === 3) return '3rd';
    return `${position}th`;
  };

  const formatItemEffect = (effect) => {
    if (!effect) return 'No effect';
    
    const effects = [];
    if (effect.heal) effects.push(`Heals ${effect.heal} HP`);
    if (effect.mana) effects.push(`Restores ${effect.mana} MP`);
    if (effect.dot) effects.push(`Deals ${effect.dot} damage per tick for ${effect.ticks} ticks`);
    if (effect.attackBoost) effects.push(`+${Math.round(effect.attackBoost * 100)}% attack for ${effect.durationSec}s`);
    if (effect.defenseBoost) effects.push(`+${Math.round(effect.defenseBoost * 100)}% defense for ${effect.durationSec}s`);
    if (effect.speedBoost) effects.push(`+${Math.round(effect.speedBoost * 100)}% speed for ${effect.durationSec}s`);
    
    return effects.length > 0 ? effects.join(', ') : 'Special effect';
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 1: return 'text-gray-400';
      case 2: return 'text-green-400';
      case 3: return 'text-blue-400';
      case 4: return 'text-purple-400';
      case 5: return 'text-yellow-400';
      default: return 'text-gray-400';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'healing': return '❤️';
      case 'mana': return '💙';
      case 'poison': return '☠️';
      case 'strength': return '💪';
      case 'defense': return '🛡️';
      case 'speed': return '⚡';
      default: return '📦';
    }
  };

  const getItemIconPath = (themeId, iconKey) => {
    if (!themeId || !iconKey) return null;
    
    const awsRegion = process.env.REACT_APP_AWS_REGION || 'us-east-1';
    const s3BasePath = themeId ? `https://soulwars.s3.${awsRegion}.amazonaws.com/themes/${themeId}` : '';
    return `${s3BasePath}/items/${iconKey}.png`;
  };

  const getItemData = (themeItemId) => {
    return items[themeItemId] || null;
  };

  const getItemEffectText = (itemData, lootEntry) => {
    return formatItemEffect(itemData?.effect || lootEntry?.itemEffect);
  };


  if (loading && battles.length === 0) {
    return (
      <div className="bg-gray-800 rounded-lg p-6">
        <h3 className="text-xl font-bold text-white mb-4">Battle History</h3>
        <div className="flex justify-center items-center h-32">
          <div className="text-gray-400">Loading battle history...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-lg p-6">
      <h3 className="text-xl font-bold text-white mb-4">Battle History</h3>
      
      {/* Battle Statistics */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-400">{stats.total_battles}</div>
            <div className="text-sm text-gray-400">Total Battles</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-400">{stats.wins}</div>
            <div className="text-sm text-gray-400">Wins</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-400">{stats.total_eliminations}</div>
            <div className="text-sm text-gray-400">Eliminations</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-cyan-400">{stats.total_xp_earned.toLocaleString()}</div>
            <div className="text-sm text-gray-400">XP Earned</div>
          </div>
        </div>
      )}

      {/* Battle History Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-600">
              <th className="text-left py-2 text-gray-300">Date</th>
              <th className="text-left py-2 text-gray-300">Position</th>
              <th className="text-left py-2 text-gray-300">Elims</th>
              <th className="text-left py-2 text-gray-300">XP</th>
              <th className="text-left py-2 text-gray-300">Duration</th>
            </tr>
          </thead>
          <tbody>
            {battles.map((battle, index) => (
              <tr key={battle.id}>
                <td colSpan="5" className="p-0">
                  <div className="bg-gray-800/50 border border-gray-600/50 rounded-lg m-2 hover:border-gray-500 hover:bg-gray-700/30 transition-all duration-200 shadow-lg">
                    {/* Main battle stats row */}
                    <div className="grid grid-cols-5 gap-0 border-b border-gray-600/30">
                      <div className="py-3 px-3 text-gray-300 border-r border-gray-600/30">
                        {battle.battle_id ? (
                          <Link
                            to={`/battle/${battle.battle_id}`}
                            className="text-blue-300 hover:text-blue-200 cursor-pointer"
                            title={formatDate(battle.battle_timestamp)}
                          >
                            {formatTimeElapsed(battle.battle_timestamp)}
                          </Link>
                        ) : (
                          <span
                            className="cursor-help"
                            title={formatDate(battle.battle_timestamp)}
                          >
                            {formatTimeElapsed(battle.battle_timestamp)}
                          </span>
                        )}
                      </div>
                      <div className={`py-3 px-3 ${getPositionColor(battle.final_position)} border-r border-gray-600/30`}>
                        {getPositionText(battle.final_position)}
                      </div>
                      <div className="py-3 px-3 text-white border-r border-gray-600/30">
                        {battle.eliminations_count}
                      </div>
                      <div className="py-3 px-3 text-yellow-400 border-r border-gray-600/30">
                        {battle.xp_earned.toLocaleString()}
                      </div>
                      <div className="py-3 px-3 text-gray-300">
                        {battle.battle_duration_seconds ? `${Math.floor(battle.battle_duration_seconds / 60)}m ${battle.battle_duration_seconds % 60}s` : 'N/A'}
                      </div>
                    </div>
                    {/* Loot row */}
                    <div className="py-3 px-4 bg-gray-800/20">
                      <div className="flex items-center space-x-4">
                        <span className="text-xs text-gray-400 font-medium">Loot:</span>
                        <div className="flex items-center space-x-3">
                          {battle.loot && battle.loot.length > 0 ? (
                            battle.loot
                              .filter(item => item != null) // Filter out null items
                              .slice()
                              .sort((a, b) => {
                                const aTypeScore = a.lootType === 'token' ? 0 : 1;
                                const bTypeScore = b.lootType === 'token' ? 0 : 1;
                                if (aTypeScore !== bTypeScore) {
                                  return aTypeScore - bTypeScore;
                                }

                                if (a.lootType === 'token' && b.lootType === 'token') {
                                  const aSymbol = (a.tokenSymbol || '').toLowerCase();
                                  const bSymbol = (b.tokenSymbol || '').toLowerCase();
                                  return aSymbol.localeCompare(bSymbol);
                                }

                                const aData = getItemData(a.themeItemId);
                                const bData = getItemData(b.themeItemId);
                                const aTier = aData?.tier ?? 0;
                                const bTier = bData?.tier ?? 0;
                                if (bTier !== aTier) return bTier - aTier;

                                const aName = (a.itemName || '').toLowerCase();
                                const bName = (b.itemName || '').toLowerCase();
                                return aName.localeCompare(bName);
                              })
                              .map((item, lootIndex) => (
                              <div key={lootIndex} className="flex items-center space-x-1">
                                {item && item.lootType === 'item' ? (
                                  (() => {

                                    let reconciledItemId = item.themeItemId;
                                    //Trim out the tier suffix from the itemId ie "potion_strength_t4" to "potion_strength"
                                    reconciledItemId = reconciledItemId.replace(/_t[1-5]$/, '');
                                    
                                    const itemData = getItemData(item.themeItemId);
                                    const iconPath = itemData ? formatItemIcon(battle.theme_id, itemData.iconKey) : null;
                                    const itemAmount = item.amount || item.qty || 1;
                                    
                                    return (
                                      <div 
                                        className="flex items-center space-x-1 bg-blue-900/30 px-2 py-1 rounded-md border border-blue-700/30 cursor-help"
                                        title={itemData ? 
                                          `${itemData.name}\n${itemAmount > 1 ? `Quantity: ${itemAmount}` : ''}\nType: ${itemData.type}\nTier: ${itemData.tier}\n${getItemEffectText(itemData, item) || 'Effect: None'}` : 
                                          [
                                            item.notes || `Item #${item.themeItemId}`,
                                            getItemEffectText(null, item) ? `Effect: ${getItemEffectText(null, item)}` : null
                                          ].filter(Boolean).join('\n')
                                        }
                                      >
                                        {iconPath ? (
                                          <img 
                                            src={iconPath} 
                                            alt={itemData?.name || 'Item'}
                                            className="w-4 h-4"
                                            onError={(e) => {
                                              e.target.style.display = 'none';
                                              e.target.nextSibling.style.display = 'inline';
                                            }}
                                          />
                                        ) : null}
                                        <span 
                                          className="text-blue-400"
                                          style={{ display: iconPath ? 'none' : 'inline' }}
                                        >
                                          {itemData ? getTypeIcon(itemData.type) : '📦'}
                                        </span>
                                        <span className={`text-xs ${itemData ? getTierColor(itemData.tier) : 'text-blue-300'}`}>
                                          {itemAmount > 1 ? `x${itemAmount}` : (itemData?.amount || '')}
                                        </span>
                                      </div>
                                    );
                                  })()
                                ) : item && item.lootType === 'token' ? (
                                  <div className="flex items-center space-x-1 bg-yellow-900/30 px-2 py-1 rounded-md border border-yellow-700/30">
                                    <span className="text-yellow-400">💰</span>
                                    <span className="text-yellow-300 text-xs">
                                      {item.amount || item.qty || 0} {item.tokenSymbol ? `$${item.tokenSymbol}` : ''}
                                    </span>
                                  </div>
                                ) : null}
                              </div>
                            ))
                          ) : (
                            <span className="text-gray-500 text-xs">No loot obtained</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Load More Button */}
      {hasMore && (
        <div className="mt-4 text-center">
          <button
            onClick={loadMore}
            disabled={loading}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              loading
                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            {loading ? 'Loading...' : 'Load More Battles'}
          </button>
        </div>
      )}

      {battles.length === 0 && !loading && (
        <div className="text-center py-8 text-gray-400">
          No battle history found for this hero.
        </div>
      )}
    </div>
  );
};

export default BattleHistoryTable;
